// =====================================================
// ROLE TEMPLATES PAGE
// TMOS Platform - Dedicated page for role template management
// =====================================================

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../state/auth/AuthProvider';
import { useLanguage } from '../../state/language/LanguageProvider';
import { FlexibleCRUDTable } from '../../components/common/FlexibleCRUDTable';
import { TemplateVisibilityDropdown } from '../../components/templates/TemplateVisibilityDropdown';
import { templateEntity } from '../../entities/TemplateEntity';
import { templateCRUDService } from '../../services/templateCRUDService';
import { Template } from '../../services/templateService';
import { PlusIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const RoleTemplatesPage: React.FC = () => {
  const { user, isAuthenticated } = useAuth();
  const { t } = useLanguage();
  const [filters, setFilters] = useState<any>({ template_type: 'ROLE' });

  // Filter templates by current user and template type
  useEffect(() => {
    if (user?.id) {
      setFilters({ 
        created_by: user.id,
        template_type: 'ROLE'
      });
    }
  }, [user?.id]);

  // Create a custom service that includes the current filters
  const createFilteredService = () => {
    return {
      ...templateCRUDService,
      getAll: async (params: any = {}) => {
        // Merge the current filters with the params
        const mergedParams = { ...filters, ...params };
        return await templateCRUDService.getAll(mergedParams);
      }
    };
  };

  // Handle template actions
  const handleEdit = (_template: Template) => {
    // TODO: Navigate to role template editor when implemented
    toast(t('templates.comingSoon') || 'Role template editor coming soon');
  };

  const handleDelete = async (template: Template) => {
    try {
      await templateCRUDService.delete(template.id);
      toast.success(t('templates.templateDeleted') || 'Template deleted successfully');
    } catch (error) {
      console.error('Error deleting template:', error);
      toast.error(t('templates.errors.failedToDeleteTemplate') || 'Failed to delete template');
    }
  };

  const handleCreate = () => {
    // TODO: Navigate to role template creator when implemented
    toast(t('templates.comingSoon') || 'Role template creator coming soon');
  };

  const handleCopy = async (template: Template) => {
    try {
      if (!user?.id) {
        toast.error(t('templates.errors.mustBeLoggedInToCopy') || 'You must be logged in to copy templates');
        return;
      }

      // Get the original template with full details
      const originalTemplate = await templateCRUDService.findById(template.id);
      
      // Find existing copies created by current user to determine the next copy number
      const allTemplates = await templateCRUDService.findAll({ created_by: user.id, template_type: 'ROLE' });
      const baseName = originalTemplate.name;
      
      // Extract base name (remove any existing "Copy (n)" or "Copy" suffix)
      const copyPattern = /^(.+?)\s+Copy\s+\((\d+)\)$/;
      const copyOnlyPattern = /^(.+?)\s+Copy$/;
      let cleanBaseName = baseName;
      
      if (copyPattern.test(baseName)) {
        cleanBaseName = baseName.match(copyPattern)![1];
      } else if (copyOnlyPattern.test(baseName)) {
        cleanBaseName = baseName.match(copyOnlyPattern)![1];
      }
      
      // Find the highest copy number for this base name
      let maxCopyNumber = 0;
      allTemplates.forEach((t: Template) => {
        if (t.name.startsWith(cleanBaseName)) {
          const match = t.name.match(copyPattern);
          if (match) {
            const num = parseInt(match[2], 10);
            if (num > maxCopyNumber) {
              maxCopyNumber = num;
            }
          } else if (t.name === `${cleanBaseName} Copy`) {
            maxCopyNumber = Math.max(maxCopyNumber, 1);
          }
        }
      });
      
      // Generate new name
      const newName = maxCopyNumber === 0 
        ? `${cleanBaseName} Copy`
        : `${cleanBaseName} Copy (${maxCopyNumber + 1})`;
      
      // Create copy
      const copyData = {
        ...originalTemplate,
        name: newName,
        marketplace_visibility: 'DRAFT' as const,
        created_by: user.id,
        id: undefined // Remove ID so a new one is generated
      };
      
      await templateCRUDService.create(copyData);
      toast.success(t('templates.templateCopied') || 'Template copied successfully');
    } catch (error) {
      console.error('Error copying template:', error);
      toast.error(t('templates.errors.failedToCopyTemplate') || 'Failed to copy template');
    }
  };

  const handleVisibilityChange = async (template: Template, newVisibility: 'DRAFT' | 'PRIVATE' | 'PUBLIC') => {
    try {
      await templateCRUDService.update(template.id, {
        ...template,
        marketplace_visibility: newVisibility
      });
      toast.success(t('templates.visibilityUpdated') || 'Visibility updated successfully');
    } catch (error) {
      console.error('Error updating visibility:', error);
      toast.error(t('templates.errors.failedToUpdateVisibility') || 'Failed to update visibility');
    }
  };

  if (!isAuthenticated) {
    return <div>Please log in to manage templates.</div>;
  }

  // Customize entity to add visibility dropdown
  const entityWithVisibilityRenderer = {
    ...templateEntity,
    tableColumns: templateEntity.tableColumns.map((col: any) => {
      if (col.key === 'marketplace_visibility') {
        return {
          ...col,
          render: (_value: any, item: Template) => {
            return (
              <TemplateVisibilityDropdown
                template={item}
                onVisibilityChange={(newVisibility: 'DRAFT' | 'PRIVATE' | 'PUBLIC') => handleVisibilityChange(item, newVisibility)}
              />
            );
          }
        };
      }
      return col;
    })
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#004D99]">
            {t('templates.roleTemplates') || 'Role Templates'}
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            {t('templates.roleTemplatesDescription') || 'Create and manage user role templates with permissions'}
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="flex items-center gap-2 px-4 py-2 bg-[#004C94] text-[#95C4FF] rounded-lg hover:bg-[#003d7a] transition-colors"
        >
          <PlusIcon className="w-5 h-5" />
          {t('templates.createRoleTemplate') || 'Create Role Template'}
        </button>
      </div>

      <FlexibleCRUDTable
        entity={entityWithVisibilityRenderer}
        service={createFilteredService()}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onCopy={handleCopy}
      />
    </div>
  );
};

export default RoleTemplatesPage;

